import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Paper,
  Select,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import {
  IconBuildingStore,
  IconGripVertical,
  IconX,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type {
  CoinPackageDefinition,
  FurnitureDefinition,
} from "../../shared/types";
import { resolvePaletteColors } from "../../shared/furniture-palette";
import { type FurnitureInventoryEntry, type Session } from "../lib/api";
import { FurniturePreviewCanvas } from "./FurniturePreviewCanvas";

const PANEL_WIDTH = 420;
const PANEL_HEIGHT = 520;

function clampPosition(x: number, y: number): { x: number; y: number } {
  const margin = 12;
  const maxX = Math.max(margin, window.innerWidth - PANEL_WIDTH - margin);
  const maxY = Math.max(margin, window.innerHeight - PANEL_HEIGHT - margin);
  return {
    x: Math.min(Math.max(margin, x), maxX),
    y: Math.min(Math.max(margin, y), maxY),
  };
}

function initialPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 200, y: 84 };
  return clampPosition(
    window.innerWidth - PANEL_WIDTH - 20,
    window.innerHeight - PANEL_HEIGHT - 80
  );
}

interface Props {
  session: Session;
  coins: number;
  coinPackages: CoinPackageDefinition[];
  furnitureCatalog: FurnitureDefinition[];
  furnitureInventory: FurnitureInventoryEntry[];
  economyLoading: boolean;
  onRefreshEconomy: () => Promise<void>;
  onBuyCoins: (packageId: string) => Promise<void>;
  onBuyFurniture: (furnitureId: string, colorVariantId?: string) => Promise<void>;
  onSelectFurniture: (item: FurnitureDefinition, colorVariantId?: string) => void;
  onClose: () => void;
}

export function MarketplaceBrowser({
  session,
  coins,
  coinPackages,
  furnitureCatalog,
  furnitureInventory,
  economyLoading,
  onRefreshEconomy,
  onBuyCoins,
  onBuyFurniture,
  onSelectFurniture,
  onClose,
}: Props) {
  const [buyingFurnitureId, setBuyingFurnitureId] = useState<string | null>(
    null
  );
  const [buyingPackageId, setBuyingPackageId] = useState<string | null>(null);
  const [selectedVariantByFurnitureId, setSelectedVariantByFurnitureId] =
    useState<Record<string, string>>({});
  const [position, setPosition] = useState(initialPosition);
  const dragState = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const inventoryById = useMemo(() => {
    const totals = new Map<string, number>();
    for (const entry of furnitureInventory) {
      const availableQuantity = Math.max(
        0,
        entry.availableQuantity ?? entry.quantity
      );
      const prev = totals.get(entry.furnitureId) ?? 0;
      totals.set(entry.furnitureId, prev + availableQuantity);
    }
    return totals;
  }, [furnitureInventory]);
  const inventoryByVariantKey = useMemo(() => {
    const quantities = new Map<string, number>();
    for (const entry of furnitureInventory) {
      const key = `${entry.furnitureId}:${entry.colorVariantId ?? ""}`;
      const availableQuantity = Math.max(
        0,
        entry.availableQuantity ?? entry.quantity
      );
      const prev = quantities.get(key) ?? 0;
      quantities.set(key, prev + availableQuantity);
    }
    return quantities;
  }, [furnitureInventory]);

  const resolveVariantId = useCallback(
    (item: FurnitureDefinition): string | undefined => {
      const variants = (item.colorVariants ?? []).filter(
        (variant) => variant.active !== false
      );
      if (variants.length === 0) {
        return undefined;
      }
      const selected = selectedVariantByFurnitureId[item.id];
      return (
        variants.find((variant) => variant.id === selected)?.id ??
        variants.find((variant) => variant.id === item.defaultColorVariantId)?.id ??
        variants[0]?.id
      );
    },
    [selectedVariantByFurnitureId]
  );

  const resolveSelectedVariant = useCallback(
    (item: FurnitureDefinition) => {
      const variantId = resolveVariantId(item);
      if (!variantId) return undefined;
      return (item.colorVariants ?? []).find((variant) => variant.id === variantId);
    },
    [resolveVariantId]
  );
  const resolveDefaultVariantId = useCallback(
    (item: FurnitureDefinition): string | undefined => {
      const variants = (item.colorVariants ?? []).filter(
        (variant) => variant.active !== false
      );
      if (variants.length === 0) {
        return undefined;
      }
      return (
        variants.find((variant) => variant.id === item.defaultColorVariantId)?.id ??
        variants[0]?.id
      );
    },
    []
  );

  const stopDragging = useCallback(() => {
    dragState.current = null;
    document.body.style.userSelect = "";
  }, []);

  const onDragMove = useCallback((event: PointerEvent) => {
    const state = dragState.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    setPosition(clampPosition(state.originX + dx, state.originY + dy));
  }, []);

  const onDragEnd = useCallback(
    function handleDragEnd() {
      stopDragging();
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", handleDragEnd);
      window.removeEventListener("pointercancel", handleDragEnd);
    },
    [onDragMove, stopDragging]
  );

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onDragEnd);
      window.removeEventListener("pointercancel", onDragEnd);
      stopDragging();
    };
  }, [onDragEnd, onDragMove, stopDragging]);

  const handleHeaderPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
    window.addEventListener("pointercancel", onDragEnd);
  };

  const handleBuyCoins = async (packageId: string) => {
    setBuyingPackageId(packageId);
    try {
      await onBuyCoins(packageId);
      notifications.show({
        title: "Coins credited",
        message: "Purchase receipt accepted and coins added.",
        color: "teal",
      });
      await onRefreshEconomy();
    } catch (error) {
      notifications.show({
        title: "Coin purchase failed",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setBuyingPackageId(null);
    }
  };

  const handlePlaceFurniture = (item: FurnitureDefinition) => {
    onSelectFurniture(item, resolveVariantId(item));
  };

  const handleBuyFurniture = async (
    item: FurnitureDefinition,
    placeAfterBuy: boolean
  ) => {
    setBuyingFurnitureId(item.id);
    try {
      const colorVariantId = resolveVariantId(item);
      await onBuyFurniture(item.id, colorVariantId);
      notifications.show({
        title: "Furniture purchased",
        message: `${item.name} added to your inventory.`,
        color: "teal",
      });
      await onRefreshEconomy();
      if (placeAfterBuy) {
        onSelectFurniture(item, colorVariantId);
      }
    } catch (error) {
      notifications.show({
        title: "Purchase failed",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setBuyingFurnitureId(null);
    }
  };

  return (
    <Paper
      p={10}
      radius="md"
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        width: PANEL_WIDTH,
        height: PANEL_HEIGHT,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        border: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        pointerEvents: "auto",
      }}
    >
      <Box
        onPointerDown={handleHeaderPointerDown}
        style={{
          touchAction: "none",
          cursor: "grab",
          marginBottom: 6,
          padding: "6px 10px",
          userSelect: "none",
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <IconGripVertical size={14} color="rgba(0,0,0,0.3)" />
            <IconBuildingStore size={15} color="#ff922b" />
            <Title order={5} fz={13} c="gray.8">
              Asset Browser
            </Title>
            <Badge size="xs" variant="light" color="yellow">
              {coins.toLocaleString()} coins
            </Badge>
          </Group>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray.4"
            onClick={onClose}
          >
            <IconX size={15} />
          </ActionIcon>
        </Group>
      </Box>

      <Tabs
        defaultValue="furniture"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Tabs.List mb="sm">
          <Tabs.Tab value="furniture">Furniture</Tabs.Tab>
          <Tabs.Tab value="coins">Coins</Tabs.Tab>
        </Tabs.List>

        <ScrollArea style={{ flex: 1, minHeight: 0 }} offsetScrollbars>
          <Tabs.Panel value="furniture">
            <Stack gap="md" px={4}>
              {economyLoading && (
                <SimpleGrid cols={2} spacing="sm">
                  {Array.from({ length: 6 }, (_, i) => (
                    <Skeleton key={i} height={120} radius="md" />
                  ))}
                </SimpleGrid>
              )}
              {!economyLoading && (
                <SimpleGrid cols={2} spacing="sm">
                  {furnitureCatalog.map((item) => {
                    const owned = inventoryById.get(item.id) ?? 0;
                    const buying = buyingFurnitureId === item.id;
                    const isDraft = item.itemState === "draft";
                    const listedVariants = (item.colorVariants ?? []).filter(
                      (variant) => variant.active !== false
                    );
                    const selectedVariantId = resolveVariantId(item);
                    const selectedVariant = resolveSelectedVariant(item);
                    const hasVariants = (item.colorVariants?.length ?? 0) > 0;
                    const hasListedVariants = listedVariants.length > 0;
                    const unitPrice =
                      selectedVariant?.priceCoins ?? item.priceCoins;
                    const ownedForSelectedVariant =
                      hasVariants && selectedVariantId
                        ? inventoryByVariantKey.get(
                            `${item.id}:${selectedVariantId}`
                          ) ?? 0
                        : owned;
                    const defaultPreviewVariantId = resolveDefaultVariantId(item);
                    const previewPaletteColors = resolvePaletteColors(
                      item.colorVariants,
                      defaultPreviewVariantId,
                      item.defaultColorVariantId
                    );
                    return (
                      <Card
                        key={item.id}
                        shadow="xs"
                        radius="md"
                        withBorder
                        padding="xs"
                      >
                        <Box
                          mb={6}
                          style={{
                            height: 80,
                            borderRadius: 8,
                            overflow: "hidden",
                            background:
                              "radial-gradient(ellipse at 50% 80%, rgba(253,126,20,0.04) 0%, transparent 60%), linear-gradient(180deg, #edf2f7 0%, #f8f9fa 100%)",
                          }}
                        >
                          {item.modelUrl.endsWith(".glb") ? (
                            <FurniturePreviewCanvas
                              modelUrl={item.modelUrl}
                              tintColor={item.tintColor}
                              colorVariants={item.colorVariants}
                              defaultColorVariantId={item.defaultColorVariantId}
                              colorVariantId={defaultPreviewVariantId}
                              paletteColors={previewPaletteColors}
                            />
                          ) : item.thumbnailUrl ? (
                            <img
                              src={item.thumbnailUrl}
                              alt={item.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          ) : null}
                        </Box>
                        <Group justify="space-between" mb={4}>
                          <Text size="xs" fw={600} truncate>
                            {item.name}
                          </Text>
                          <Badge size="xs" variant="light" color="yellow">
                            {unitPrice}
                          </Badge>
                        </Group>
                        <Text size="xs" c="dimmed" lineClamp={2} mb={6}>
                          {item.description ?? "Placeable furniture."}
                        </Text>
                        <Group gap={6} mb={8}>
                          <Badge size="xs" variant="outline">
                            {item.blockWidth}x{item.blockDepth}
                          </Badge>
                          {item.itemState === "draft" ? (
                            <Badge size="xs" variant="light" color="orange">
                              Draft
                            </Badge>
                          ) : null}
                        </Group>
                        {hasVariants ? (
                          <Select
                            size="xs"
                            mb={8}
                            value={selectedVariantId ?? null}
                            data={listedVariants.map((variant) => ({
                              value: variant.id,
                              label:
                                typeof variant.priceCoins === "number"
                                  ? `${variant.name} (${variant.priceCoins})`
                                  : variant.name,
                            }))}
                            placeholder="Color variant"
                            disabled={!hasListedVariants}
                            onChange={(value) => {
                              if (!value) return;
                              setSelectedVariantByFurnitureId((prev) => ({
                                ...prev,
                                [item.id]: value,
                              }));
                            }}
                          />
                        ) : null}
                        {hasVariants && !hasListedVariants ? (
                          <Text size="xs" c="red">
                            No listed color variants available.
                          </Text>
                        ) : null}
                        {owned > 0 ? (
                          isDraft ? (
                            <Button
                              size="xs"
                              fullWidth
                              variant="light"
                              disabled={buying || ownedForSelectedVariant <= 0}
                              onClick={() => handlePlaceFurniture(item)}
                            >
                              Place Draft ({ownedForSelectedVariant})
                            </Button>
                          ) : (
                            <Group gap={6} grow wrap="nowrap">
                              <Button
                                size="xs"
                                variant="light"
                                disabled={
                                  buying ||
                                  ownedForSelectedVariant <= 0 ||
                                  (hasVariants && !hasListedVariants)
                                }
                                onClick={() => handlePlaceFurniture(item)}
                              >
                                Place ({ownedForSelectedVariant})
                              </Button>
                              <Button
                                size="xs"
                                loading={buying}
                                disabled={hasVariants && !hasListedVariants}
                                onClick={() => void handleBuyFurniture(item, false)}
                              >
                                Buy +1
                              </Button>
                            </Group>
                          )
                        ) : isDraft ? (
                          <Text size="xs" c="orange.7">
                            Draft sandbox item. Not purchasable.
                          </Text>
                        ) : (
                          <Button
                            size="xs"
                            fullWidth
                            loading={buying}
                            disabled={hasVariants && !hasListedVariants}
                            onClick={() => void handleBuyFurniture(item, true)}
                          >
                            Buy & Place
                          </Button>
                        )}
                      </Card>
                    );
                  })}
                </SimpleGrid>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="coins">
            <Stack gap="sm" px={4}>
              <Text size="xs" c="dimmed">
                Pay with Kukai and instantly credit your in-game balance.
              </Text>
              {coinPackages.map((pack) => (
                <Card
                  key={pack.id}
                  shadow="xs"
                  radius="md"
                  withBorder
                  padding="sm"
                >
                  <Group justify="space-between" wrap="nowrap">
                    <div>
                      <Text size="sm" fw={700}>
                        {pack.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {pack.coins.toLocaleString()} coins
                      </Text>
                    </div>
                    <Button
                      size="xs"
                      loading={buyingPackageId === pack.id}
                      onClick={() => void handleBuyCoins(pack.id)}
                    >
                      Buy ({Number(pack.tezAmountMutez) / 1_000_000} tez)
                    </Button>
                  </Group>
                </Card>
              ))}
            </Stack>
          </Tabs.Panel>
        </ScrollArea>
      </Tabs>
    </Paper>
  );
}
